<template>
  <reduced-layout-fi
    class="login-form-page-fi"
    :brand="brand"
    :api-data="apiData || undefined"
    :logo-src="apiData?.Header_Logo?.Src"
    :logo-alt="apiData?.Header_Logo?.Alt"
    :interface-language="interfaceLanguage || undefined"
    @change-language="changeLanguage"
  >
    <login-form-field-fi
      v-if="isMounted"
      ref="loginForm"
      :password="form.password"
      :username="form.username"
    />
    <loading-indicator
      v-if="!isMounted || apiDataLoading"
      variant="spinnerFullPageCover"
    />
    <template v-else>
      <div v-if="isLoginFailure || isLoginImpossible">
        <error-message
          ref="errorMessage"
          :title="apiData?.Login_Failed_Title || 'Login failed'"
          scroll-to="siteLoginStandalone"
          data-test="login-error"
        >
          {{ apiData?.Login_Failed_Error || '' }}
        </error-message>
      </div>
      <div
        v-if="!isLoginImpossible"
        id="siteLoginStandalone"
        class="login-form-page-fi__login"
      >
        <div
          v-if="apiData"
          class="login-form-page-fi__form"
          data-test="login-form"
        >
          <headline-fi
            class="login-form-page-fi__headline fi-text-size-df-30"
            headline-tag="h1"
          >
            {{ apiData.Headline }}
          </headline-fi>

          <text-input-fi
            id="email"
            ref="username"
            v-model.trim="form.username"
            class="login-form-page-fi__username"
            autocomplete="username"
            name="username"
            data-test="email-input"
            :rules="
              userIdentifierRule(brand === 'fite', form.username, enableUserIdentifierValidation)
            "
            :disabled="isLoginImpossible"
            :label="apiData.Email_Placeholder"
            @keyup="validateCharacters(form.username, 'email')"
            @blur="enableUserIdentifierValidation = true"
            @update:model-value="onChangeUsername"
            @validation-changed="
              (value) => {
                form.usernameValid = value;
              }
            "
            @keyup-enter="onEnterEmail"
          />
          <text-input-fi
            id="password"
            ref="password"
            v-model="form.password"
            class="login-form-page-fi__password"
            autocomplete="current-password"
            name="password"
            type="password"
            data-test="password-input"
            :rules="passwordChangedOrBlurred ? rules.required : []"
            :disabled="isLoginImpossible"
            :label="apiData.Password_Placeholder"
            @update:model-value="onChangePassword"
            @blur="passwordChangedOrBlurred = true"
            @validation-changed="
              (value) => {
                form.passwordValid = value;
              }
            "
            @keyup-enter="onEnterPassword"
          />

          <div v-if="apiData.Password_Reset">
            <link-fi
              :href="apiData.Password_Reset.Url"
              :target="apiData.Password_Reset.Target"
              :disabled="isLoginImpossible"
              data-test="req-new-cred"
              class="login-form-page-fi__password-reset fi-text-size-df-18"
            >
              {{ apiData.Password_Reset.Text }}
            </link-fi>
          </div>

          <button-fi
            type="submit"
            class="button-fi__primary button-fi--stretched button-fi--large"
            data-test="login-button"
            :loading="isLoading"
            :disabled="submitDisabled || isLoginImpossible"
            @click="submit"
          >
            {{ apiData.Submit }}
          </button-fi>

          <div
            v-if="apiData.Registration"
            class="login-form-page-fi__registration fi-text-size-df-18"
          >
            {{ apiData.Registration_Headline }}
            <button-fi
              :target="apiData.Registration.Target"
              :href="apiData.Registration.Url"
              :loading="isLoading"
              :disabled="isLoginImpossible"
              class="button-fi__tertiary button-fi--stretched button-fi--large login-form-page-fi__registration-btn"
              data-test="registration-btn"
              natural-events
            >
              {{ apiData.Registration.Text }}
            </button-fi>
          </div>
        </div>
      </div>
    </template>
  </reduced-layout-fi>
</template>

<script setup lang="ts">
import { ButtonFi, ErrorMessage, HeadlineFi, LinkFi, LoadingIndicator, TextInputFi } from 'atoms';
import { computed, nextTick, onMounted, reactive, ref } from 'vue';

import LoginFormFieldFi from '@/components/login-dialog-fi/LoginFormFieldFi.vue';
import ReducedLayoutFi from '@/components/reduced-layout-fi/ReducedLayoutFi.vue';
import { useCookies } from '@/composables/cookies';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import {
  getCookiesFunction,
  handleDebugFlagFunction,
  setCookiesFunction,
  siteLoginDebugKey,
  useLogin,
} from '@/composables/login';
import { useValidators } from '@/composables/validators';
import Validators from '@/utils/validators';

interface FormData {
  password: string;
  passwordValid: boolean;
  usernameValid: boolean;
  username: string;
}

interface ApiData {
  Headline?: string;
  Email_Placeholder?: string;
  Password_Placeholder?: string;
  Password_Reset?: {
    Url: string;
    Target: string;
    Text: string;
  };
  Submit?: string;
  Registration?: {
    Url: string;
    Target: string;
    Text: string;
  };
  Registration_Headline?: string;
  Login_Failed_Title?: string;
  Login_Failed_Error?: string;
  Mandatory_Error?: string;
  Invalid_Email_Error?: string;
  Available_Languages?: Array<{ Title: string; Value: string }>;
  Header_Logo?: {
    Src: string;
    Alt: string;
  };
}

interface LoginCookies extends Record<string, string | undefined> {
  lang?: string;
  brand?: string;
  apiKey?: string;
  clientId?: string;
  username?: string;
}

const loginRedirectCookie = 'dialog-redirect';

const queryToObject = (queryString: string): Record<string, string> => {
  const searchParams = new URLSearchParams(queryString);
  return Object.fromEntries(searchParams.entries());
};

const fields = useFields('LoginFormPageFiFields');

const fischer = useFischer();
const dict = useDict();
const { cookies } = useCookies();
const { goToLoginPage, initializeAuthorization } = useLogin();

const errorMessage = ref<InstanceType<typeof ErrorMessage>>();
const username = ref<InstanceType<typeof TextInputFi>>();
const password = ref<InstanceType<typeof TextInputFi>>();
const loginForm = ref<InstanceType<typeof LoginFormFieldFi>>();
const { validateCharacters, userIdentifierRule } = useValidators();

const form = reactive<FormData>({
  password: '',
  passwordValid: false,
  usernameValid: false,
  username: '',
});

const usernameChangedOrBlurred = ref(false);
const passwordChangedOrBlurred = ref(false);
const isLoading = ref(false);
const isLoginImpossible = ref(false);
const enableUserIdentifierValidation = ref(false);
const isLoginFailure = ref(false);
const brand = ref('');
const lang = ref('');
const interfaceLanguage = ref<string | null>(null);
const apiData = ref<ApiData | null>(null);
const apiDataLoading = ref(true);
const isSubmitting = ref(false);
const isMounted = ref(false);
const isDebug = ref(false);
const clientId = ref('');
const logDebugWrapper = ref<(...logs: any[]) => void>(() => {});

const formValid = computed(() => form.passwordValid && form.usernameValid);

const rules = computed(() => {
  return Validators(
    {
      ...dict,
      get: (key: string) =>
        ({
          'online-Shop.Global-Labels.Error-Message-Required': apiData.value?.Mandatory_Error,
        })[key] || key,
    },
    {
      emailFieldValidationMsgInvalidFormat: {
        value: apiData.value?.Invalid_Email_Error,
      },
    },
  );
});

const submitDisabled = computed(() => {
  return (
    isSubmitting.value ||
    isLoginImpossible.value ||
    ((passwordChangedOrBlurred.value || usernameChangedOrBlurred.value) &&
      (!formValid.value || form.password === null))
  );
});

const referrer = computed(() => {
  if (IS_SHOWROOM) {
    return fischer.query('referrer') || 'showroom';
  }
  const [, host] = /^https?:\/\/([^/]+)(?:\/|$)/.exec(window.document.referrer) || [];
  return host;
});

const referrerData = computed(() => {
  logDebug('checking referrer data', referrer.value);
  const json = fields.referrerData?.value;
  if (json) {
    try {
      const data = JSON.parse(json);
      const customData = data[referrer.value];
      if (customData) {
        logDebug('got referrer data', customData);
        return customData;
      }
    } catch (error) {
      console.error(error, json);
    }
  } else {
    logDebug('no referrer data configured');
  }
  logDebug('no referrer data');
  return {};
});

const getCookies = (): LoginCookies => {
  return getCookiesFunction(cookies, logDebugWrapper.value);
};

const setCookies = async (cookieData: LoginCookies): Promise<boolean> => {
  return setCookiesFunction(
    fischer,
    logDebugWrapper.value,
    cookies,
    () => getCookies(),
    (reason: string) => criticalLoginFailure(reason),
    cookieData,
  );
};

const criticalLoginFailure = (...errors: any[]) => {
  console.error(...errors);
  isLoginImpossible.value = true;
  isLoading.value = false;
  // this may exist or not, depending on the promises being fulfilled
  // before or after this was mounted
  if (errorMessage.value) {
    errorMessage.value.trigger();
  }
};

const logDebug = (...params: any[]) => {
  if (isDebug.value) {
    console.info(...params);
  }
};

const handleDebugFlag = () => {
  if (IS_SSR) {
    return;
  }

  (window as any).activateLoginDebug = (value: boolean) => {
    isDebug.value = value;
    cookies.set(siteLoginDebugKey, value);
  };
  if (!handleDebugFlagFunction(fischer)) {
    if (cookies.get(siteLoginDebugKey)) {
      isDebug.value = true;
    }
  }
};

const redirectFromOldService = (): boolean => {
  if (IS_SSR) {
    return false;
  }
  let newLocation = String(window.location);
  let oldServices = 'https://services.ugfischer.com/';
  let newServices = import.meta.env.VITE_OPENID_SERVICES;
  for (let level = 0; level < 3; level += 1) {
    const oldServiceRegEx = new RegExp(oldServices.replace(/\./g, '\\.'), 'g');
    newLocation = newLocation.replace(oldServiceRegEx, newServices);

    oldServices = encodeURIComponent(oldServices);
    newServices = encodeURIComponent(newServices);
  }
  if (String(window.location) !== newLocation) {
    console.debug('old services detected, redirecting to new services');
    window.location.href = newLocation;
    return true;
  }
  return false;
};

const handleLoginRequest = async (): Promise<boolean> => {
  if (IS_SSR) {
    return true;
  }
  logDebug('handling login request');

  const hash = queryToObject(window.location.hash.substr(1));
  const search = queryToObject(window.location.search.substr(1));
  const hint: Record<string, string> = {};
  if (search.login_hint) {
    // NOTE: this is not the standard way to use it
    ['api_key', 'lang', 'brand'].forEach((key) => {
      const match = new RegExp(`(?:^|\\.)${key}-([^.]+)`).exec(search.login_hint);
      if (match) {
        const [, value] = match;
        hint[key] = value;
      }
    });
  }
  const {
    lang: langParam,
    brand: brandParam,
    state,
    nonce,
    client_id: clientIdParam,
    redirect_uri: redirectUri,
    api_key: apiKey,
    scope = 'openid',
    response_type: responseType = 'id_token token',
    ui_locales: uiLocales,
    password: passwordParam,
    username: usernameParam,
  } = {
    ...search,
    ...hint,
    ...hash,
  };

  const cookieData = getCookies();

  if (langParam) {
    cookieData.lang = langParam.replace('_', '-');
  }
  if (uiLocales) {
    const [locale] = uiLocales.split(' ');
    cookieData.lang = locale.replace('_', '-');
  }
  if (brandParam) {
    cookieData.brand = brandParam;
  }
  if (apiKey) {
    cookieData.apiKey = apiKey;
  }

  let resolvedClientId = clientIdParam || cookieData.clientId;
  if (!resolvedClientId) {
    const [langShort] = (cookieData.lang || 'de').split(/[_-]/);
    resolvedClientId = `fiwe-${langShort.toLowerCase()}`;
  }
  if (resolvedClientId === 'fite-de') {
    resolvedClientId = 'fite-int';
  }
  cookieData.clientId = resolvedClientId;
  clientId.value = resolvedClientId;

  const searchParams = new URLSearchParams(window.location.search);
  const authorizeRedirectParam = new URLSearchParams(searchParams.get('authorize_redirect') || '');
  const originalRedirect = authorizeRedirectParam.get('redirect_uri');
  if (brandParam?.startsWith('dialog:') && originalRedirect) {
    logDebug('redirecting back to custom login page');
    searchParams.delete('state');
    const brandPath = /^dialog:([^?#@]+)?/.exec(brandParam)?.[1];
    const authRedirectUrl = new URL(originalRedirect);
    const newLoginRedirect = `${authRedirectUrl.origin}${brandPath}?${searchParams}${window.location.hash}`;
    cookies.set(
      loginRedirectCookie,
      newLoginRedirect,
      0,
      '/',
      undefined,
      fischer.isSecureConnection(),
      'None',
    );
    window.location.href = newLoginRedirect;
    return true;
  } else {
    logDebug('not a custom login page', brandParam);
    if (brandParam) {
      logDebug('removing login-redirect');
      cookies.remove(loginRedirectCookie);
    }
  }

  if (!(await setCookies(cookieData))) {
    return true;
  }

  if (redirectUri && state) {
    if (usernameParam && passwordParam) {
      logDebug('attempting to submit login in a second');
      form.username = usernameParam;
      form.password = passwordParam;
      apiData.value = {};
      apiDataLoading.value = false;

      setTimeout(() => {
        logDebug('attempting to submit login in a second');
        if (loginForm.value) {
          submit();
        } else {
          console.error('no refs.loginForm');
        }
      }, 1000);

      return true;
    }

    goToLoginPage(state, nonce, scope, responseType, redirectUri);
    return true;
  }

  logDebug('Did not get login request from hash');
  return false;
};

const loadApiData = async (langParam: string, apiKeyParam?: string) => {
  try {
    apiDataLoading.value = true;

    const langEncoded = encodeURIComponent(langParam);

    const apiKey = (
      apiKeyParam ||
      fields.defaultApiKey?.value ||
      (IS_SHOWROOM && (fischer.query('api_key') || 'fiwe')) ||
      ''
    ).replace(/[^A-Z0-9a-z-]/g, '');

    let url = '/sso/api/app';
    if (IS_SHOWROOM) {
      url = `/__mocks__/sitecoreApi${url}/mock_${langEncoded}_${apiKey}.json`;
    } else {
      url = `${url}?lang=${langEncoded}&apiKey=${apiKey}`;
    }
    logDebug('loading api data with apiKey', apiKey);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch api data: ${response.status}`);
    }
    const data = await response.json();
    if (data.Submit) {
      apiData.value = data;
      logDebug('got api data');
    } else {
      if (langParam !== 'en') {
        console.info(`Could not load data for '${langParam}' in '${apiKeyParam}', fallback to en`);
        await loadApiData('en', apiKeyParam);
      } else {
        throw new Error('Could not load en api data');
      }
    }

    const languages = apiData.value?.Available_Languages || [];
    if (languages.some((pageLang) => pageLang.Value === langParam)) {
      interfaceLanguage.value = langParam;
    }
  } catch (error) {
    criticalLoginFailure('Failed to load api', error);
  } finally {
    apiDataLoading.value = false;
  }
};

const handleLoginFormPage = () => {
  logDebug('handling login form page');

  const cookie = getCookies();

  const {
    brand: cookieBrand = 'fiwe',
    clientId: cookieClientId,
    apiKey,
    lang: cookieLang,
    username: cookieUsername,
  } = {
    ...cookie,
    ...referrerData.value,
  };
  brand.value = cookieBrand;
  lang.value = cookieLang || getBrowserLang();

  if (cookieClientId) {
    clientId.value = cookieClientId;
  } else {
    clientId.value = `${brand.value}-${lang.value.replace(/[_-].*/, '').toLowerCase()}`;
  }

  logDebug(`determined brand="${cookieBrand}" apiKey="${apiKey}" clientId="${clientId.value}"`);

  const usernameValue = fischer.query('username') || cookieUsername;
  if (usernameValue) {
    form.username = usernameValue;
  }

  if (
    cookieBrand !== cookie.brand ||
    clientId.value !== cookie.clientId ||
    apiKey !== cookie.apiKey ||
    usernameValue !== cookieUsername
  ) {
    logDebug('updating cookie');
    setCookies({
      ...cookie,
      brand: cookieBrand,
      clientId: clientId.value,
      apiKey,
      username: usernameValue,
      ...(cookieLang ? { lang: cookieLang } : {}),
    });
  }

  const queryError = fischer.query('error');
  if (queryError === 'bad_credentials' || queryError === 'account_disabled') {
    const loginRedirect = cookies.get(loginRedirectCookie);
    logDebug('authentication error', loginRedirect);
    if (loginRedirect && loginRedirect !== 'null') {
      logDebug('authentication_error, redirecting');
      window.location.href = loginRedirect.replace('?', '?authentication_error=true&');
      return true;
    }
    isLoginFailure.value = true;
  }

  // do not await, let spinner show
  loadApiData(lang.value, apiKey);

  logDebug('handled login form page successfully');
  return true;
};

const onChangeUsername = (value: string) => {
  form.username = value;
  usernameChangedOrBlurred.value = true;
};

const onChangePassword = (value: string) => {
  passwordChangedOrBlurred.value = true;
  form.password = value;
};

const changeLanguage = ({ value: langValue }: { value: string }) => {
  if (typeof langValue !== 'string') {
    return;
  }
  interfaceLanguage.value = langValue;

  const { apiKey } = getCookies();
  loadApiData(langValue, apiKey);
};

const getBrowserLang = (): string => {
  const fallback = 'en';

  const languages = apiData.value?.Available_Languages;
  if (!languages) {
    return fallback;
  }
  const browserLang = window.navigator.language;
  if (!browserLang) {
    return fallback;
  }

  const directMatch = languages.find((lang) => lang.Value === browserLang);
  if (directMatch) {
    return directMatch.Value;
  }

  const shortLang = browserLang.replace(/-.*/, '');
  const shortMatch = languages.find((lang) => lang.Value.startsWith(shortLang));
  if (shortMatch) {
    return shortMatch.Value;
  }

  return fallback;
};

const onEnterEmail = () => {
  if (!form.username) {
    return;
  }
  password.value?.focus();
};

const onEnterPassword = () => {
  if (!form.username || !form.password || !formValid.value) {
    logDebug(
      'cancelling password-enter',
      Boolean(form.username),
      Boolean(form.password),
      formValid.value,
    );
    return;
  }
  submit();
};

const submit = async () => {
  if (isSubmitting.value) {
    console.error('already submitting');
    return;
  }
  isSubmitting.value = true;
  const cookie = getCookies();
  if (cookie.username !== form.username) {
    setCookies({
      ...cookie,
      username: form.username,
    });
  }
  // wait for input to be mirrored in the cloned form
  nextTick(() => {
    loginForm.value?.submit();
  });
};

const createLogin = async () => {
  if (fields.disableLogin?.value) {
    isLoginImpossible.value = true;
  }
  try {
    // Initialize debug flag and wrapper
    handleDebugFlag();
    logDebugWrapper.value = (...logs: any[]) => logDebug(...logs);

    if (redirectFromOldService()) {
      return;
    }
    const brandParam = fischer.query('brand');
    if (brandParam && !brandParam.startsWith('dialog:')) {
      brand.value = brandParam;
    }

    if (IS_SSR) {
      return;
    }
    if (!(await handleLoginRequest())) {
      handleLoginFormPage();
    }
  } catch (error) {
    console.error('Error in login form page created hook', error);
  }
};

onMounted(() => {
  isMounted.value = true;
  const userName = fischer.postBody('username');
  const password = fischer.postBody('password');
  if (userName && password) {
    form.username = userName;
    form.password = password;
    nextTick().then(submit);
  } else if (fischer.query('init-login')) {
    initializeAuthorization();
  } else {
    createLogin();
  }
});
</script>

<style scoped src="./login-form-page-fi" />
